import { createContext, Dispatch, MutableRefObject, SetStateAction } from "react";

import { Tree } from "components/objectTree/types";
import { ModelTreeNodeData } from "features/modelTree/types";
import { emptyModelTree } from "features/modelTree/utils";

export const TreeContext = createContext<Tree<ModelTreeNodeData>>(emptyModelTree);
export const SetTreeContext = createContext<Dispatch<SetStateAction<Tree<ModelTreeNodeData>>> | undefined>(undefined);

/**
 * A ref holding the object tree as eagerly built in the background, before the
 * user first opens the Object Tree: the root level (top categories) plus, during
 * idle time, deeper folders (loaded but kept collapsed).
 *
 * Exposed as a ref rather than a reactive value on purpose — the background
 * preload mutates it many times, and consumers only read it non-reactively (when
 * seeding their initial tree or resetting after a search), so they never need to
 * re-render as the preload progresses. `ref.current` is `null` until the root
 * preload completes (or when the preload is skipped, e.g. offline).
 */
export const PreloadedTreeContext = createContext<MutableRefObject<Tree<ModelTreeNodeData> | null> | null>(null);

/**
 * Reactive companion to {@link PreloadedTreeContext}: flips from `false` to `true`
 * exactly once the background preload's root level (top categories) has loaded.
 *
 * The preloaded tree itself is a non-reactive ref (see above) so consumers can seed
 * their initial state from it without subscribing to every background update. But
 * that means a consumer that mounts *before* the root preload resolves (e.g. the
 * user opens the Object Tree panel within the first moment of a scene load) would
 * otherwise seed from an empty tree and never get another chance to pick up the
 * preloaded data. This flag lets such consumers react once, right when the ref
 * first becomes usable, without subscribing to Phase 2's much more frequent updates.
 */
export const PreloadedTreeRootReadyContext = createContext<boolean>(false);
